import { useState } from 'react';
import { Mail, MapPin, Building2, CheckCircle2, Calendar, FileText, Send, Upload, X } from 'lucide-react';

interface QuoteFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  projectName: string;
  projectAddress: string;
  city: string;
  state: string;
  zipCode: string;
  buildingType: string;
  occupancyType: string;
  squareFootage: string;
  numberOfFloors: string;
  ceilingHeight: string;
  systemType: string;
  existingSystem: string;
  deviceCount: string;
  notificationAppliances: string;
  projectTimeline: string;
  budget: string;
  additionalRequirements: string;
  files: File[];
}

interface FormErrors {
  [key: string]: string;
}

interface QuoteFormProps {
  onStatusChange: (status: 'idle' | 'success' | 'error') => void;
}

export function QuoteForm({ onStatusChange }: QuoteFormProps) {
  const [formData, setFormData] = useState<QuoteFormData>({
    name: '', email: '', phone: '', company: '', projectName: '', projectAddress: '',
    city: '', state: 'CA', zipCode: '', buildingType: 'commercial-office', occupancyType: 'business',
    squareFootage: '', numberOfFloors: '', ceilingHeight: '', systemType: 'addressable',
    existingSystem: 'none', deviceCount: '', notificationAppliances: '', projectTimeline: '1-3-months',
    budget: 'not-sure', additionalRequirements: '', files: []
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }
    if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required';
    if (!formData.projectAddress.trim()) newErrors.projectAddress = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Invalid ZIP code';
    }
    if (!formData.squareFootage.trim()) newErrors.squareFootage = 'Square footage is required';
    if (!formData.numberOfFloors.trim()) newErrors.numberOfFloors = 'Number of floors is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    onStatusChange('idle');
    
    try {
      // Prepare submission data (exclude files for JSON serialization)
      const submissionData = {
        ...formData,
        files: formData.files.map(f => ({ name: f.name, size: f.size, type: f.type })),
        submittedAt: new Date().toISOString(),
        id: `quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };

      // Try API submission first
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
      let apiSuccess = false;
      
      try {
        const response = await fetch(`${apiUrl}/quotes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData),
        });
        apiSuccess = response.ok;
      } catch {
        // API not available, continue with localStorage fallback
      }

      // Always store locally as backup
      const existingQuotes = JSON.parse(localStorage.getItem('quoteSubmissions') || '[]');
      existingQuotes.push({ ...submissionData, syncedToServer: apiSuccess });
      localStorage.setItem('quoteSubmissions', JSON.stringify(existingQuotes));

      onStatusChange('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({
          name: '', email: '', phone: '', company: '', projectName: '', projectAddress: '',
          city: '', state: 'CA', zipCode: '', buildingType: 'commercial-office', occupancyType: 'business',
          squareFootage: '', numberOfFloors: '', ceilingHeight: '', systemType: 'addressable',
          existingSystem: 'none', deviceCount: '', notificationAppliances: '', projectTimeline: '1-3-months',
          budget: 'not-sure', additionalRequirements: '', files: []
        });
        setErrors({});
      }, 3000);
    } catch (error) {
      console.error('Quote submission error:', error);
      onStatusChange('error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData(prev => ({ ...prev, files: [...prev.files, ...newFiles] }));
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8">
      {/* Contact Information */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
          <Mail className="w-6 h-6 mr-2 text-blue-600" />
          Contact Information
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="John Smith"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ABC Construction"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="john@example.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phone ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="(818) 555-0123"
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Project Information */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
          <MapPin className="w-6 h-6 mr-2 text-blue-600" />
          Project Information
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.projectName ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="Downtown Office Building"
            />
            {errors.projectName && <p className="mt-1 text-sm text-red-600">{errors.projectName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Project Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="projectAddress"
              value={formData.projectAddress}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.projectAddress ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="123 Main Street"
            />
            {errors.projectAddress && <p className="mt-1 text-sm text-red-600">{errors.projectAddress}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.city ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="Los Angeles"
              />
              {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="CA">California</option>
                <option value="AZ">Arizona</option>
                <option value="NV">Nevada</option>
                <option value="OR">Oregon</option>
                <option value="WA">Washington</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                ZIP Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.zipCode ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="90001"
              />
              {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Building Details */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
          <Building2 className="w-6 h-6 mr-2 text-blue-600" />
          Building Details
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Building Type</label>
              <select
                name="buildingType"
                value={formData.buildingType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="commercial-office">Commercial Office</option>
                <option value="retail">Retail</option>
                <option value="healthcare">Healthcare</option>
                <option value="educational">Educational</option>
                <option value="industrial">Industrial</option>
                <option value="warehouse">Warehouse</option>
                <option value="mixed-use">Mixed Use</option>
                <option value="residential">Residential (Multi-family)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Occupancy Type</label>
              <select
                name="occupancyType"
                value={formData.occupancyType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="assembly">Assembly (A)</option>
                <option value="business">Business (B)</option>
                <option value="educational">Educational (E)</option>
                <option value="factory">Factory (F)</option>
                <option value="high-hazard">High Hazard (H)</option>
                <option value="institutional">Institutional (I)</option>
                <option value="mercantile">Mercantile (M)</option>
                <option value="residential">Residential (R)</option>
                <option value="storage">Storage (S)</option>
                <option value="utility">Utility (U)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Square Footage <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="squareFootage"
                value={formData.squareFootage}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.squareFootage ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="10000"
              />
              {errors.squareFootage && <p className="mt-1 text-sm text-red-600">{errors.squareFootage}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Number of Floors <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="numberOfFloors"
                value={formData.numberOfFloors}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.numberOfFloors ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="3"
              />
              {errors.numberOfFloors && <p className="mt-1 text-sm text-red-600">{errors.numberOfFloors}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Ceiling Height (ft)</label>
              <input
                type="number"
                name="ceilingHeight"
                value={formData.ceilingHeight}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* System Requirements */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
          <CheckCircle2 className="w-6 h-6 mr-2 text-blue-600" />
          System Requirements
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">System Type</label>
              <select
                name="systemType"
                value={formData.systemType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="addressable">Addressable</option>
                <option value="conventional">Conventional</option>
                <option value="wireless">Wireless</option>
                <option value="voice-evacuation">Voice Evacuation</option>
                <option value="mass-notification">Mass Notification</option>
                <option value="not-sure">Not Sure</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Existing System</label>
              <select
                name="existingSystem"
                value={formData.existingSystem}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="none">No Existing System</option>
                <option value="upgrade">Upgrade Existing</option>
                <option value="replace">Replace Existing</option>
                <option value="expand">Expand Existing</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Estimated Device Count</label>
              <input
                type="number"
                name="deviceCount"
                value={formData.deviceCount}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Notification Appliances</label>
              <input
                type="number"
                name="notificationAppliances"
                value={formData.notificationAppliances}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="25"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline & Budget */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
          <Calendar className="w-6 h-6 mr-2 text-blue-600" />
          Timeline & Budget
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Project Timeline</label>
              <select
                name="projectTimeline"
                value={formData.projectTimeline}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="immediate">Immediate (Less than 1 month)</option>
                <option value="1-3-months">1-3 Months</option>
                <option value="3-6-months">3-6 Months</option>
                <option value="6-12-months">6-12 Months</option>
                <option value="planning">Planning Phase</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Budget Range</label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="not-sure">Not Sure</option>
                <option value="under-10k">Under $10,000</option>
                <option value="10k-25k">$10,000 - $25,000</option>
                <option value="25k-50k">$25,000 - $50,000</option>
                <option value="50k-100k">$50,000 - $100,000</option>
                <option value="over-100k">Over $100,000</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
          <FileText className="w-6 h-6 mr-2 text-blue-600" />
          Additional Information
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Additional Requirements or Notes
            </label>
            <textarea
              name="additionalRequirements"
              value={formData.additionalRequirements}
              onChange={handleChange}
              rows={6}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Please provide any additional details about your project..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Upload Plans or Documents (Optional)
            </label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-2">Drag and drop files here, or click to select</p>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="file-upload"
                className="inline-block px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg cursor-pointer transition-colors duration-200"
              >
                Select Files
              </label>
              <p className="text-xs text-slate-500 mt-2">Accepted: PDF, DWG, DXF, JPG, PNG (Max 10MB each)</p>
            </div>
            {formData.files.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-colors duration-200 flex items-center justify-center ${
          isSubmitting ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Request Quote
          </>
        )}
      </button>
    </form>
  );
}
